import * as ExcelJS from 'exceljs';
import i18n from '../locales/i18n';
import { ValuationTradeDetail } from '../services/api/commodityValuationService';

/**
 * Export commodity valuation batch trades to Excel with P&L formulas.
 *
 * Column layout (mirrors AG Grid + formula helper columns):
 *   A  Trade Number          (value)
 *   B  Entity                (value)
 *   C  Product               (value)
 *   D  Underlying            (value)
 *   E  Direction             (value: Buy/Sell)
 *   F  Counterparty          (value)
 *   G  Lots                  (value)
 *   H  Lot Size Multiplier   (value — formula input)
 *   I  QCF                   (value — formula input)
 *   J  Price                 (value — entry price)
 *   K  Quantity              (value)
 *   L  Fixing Ref            (value)
 *   M  Market Price          (value)
 *   N  Unrealised P&L        ** FORMULA **
 *   O  Currency              (value)
 *   P  Maturity Date         (value)
 *
 * P&L formula:  =IF(E{r}="Buy",1,-1) * G{r} * H{r} * I{r} * (M{r} - J{r})
 */
export async function exportCommodityValuationToExcel(
  trades: ValuationTradeDetail[],
  valuationDate: string,
): Promise<void> {
  const t = (key: string, fallback?: string) =>
    i18n.t(key, { defaultValue: fallback ?? key });

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(t('commodities.valuation.tabValuation', 'Valuation'));

  const totalCols = 16;

  // ── Title row (row 1) ──
  ws.getCell('A1').value = `${t('commodities.valuation.title', 'Commodity Valuation')} — ${valuationDate}`;
  ws.mergeCells(1, 1, 1, totalCols);
  ws.getCell('A1').font = { bold: true, size: 14 };
  ws.getCell('A1').alignment = { horizontal: 'left' };

  // ── Methodology row (row 2) ──
  ws.getCell('A2').value = 'P&L = Direction × Lots × Lot Size × QCF × (Market Price − Price)';
  ws.getCell('A2').font = { italic: true, size: 10, color: { argb: 'FF888888' } };
  ws.mergeCells(2, 1, 2, 10);

  // ── Header definitions ──
  const headers = [
    /* A  */ t('commodities.valuation.tradeNumber', 'Trade Number'),
    /* B  */ t('commodities.valuation.entity', 'Entity'),
    /* C  */ t('commodities.valuation.product', 'Product'),
    /* D  */ t('commodities.valuation.underlying', 'Underlying'),
    /* E  */ t('commodities.valuation.direction', 'Direction'),
    /* F  */ t('commodities.valuation.counterparty', 'Counterparty'),
    /* G  */ t('commodities.valuation.lots', 'Lots'),
    /* H  */ t('commodities.valuation.lotSizeMultiplier', 'Lot Size'),
    /* I  */ t('commodities.valuation.qcf', 'QCF'),
    /* J  */ t('commodities.valuation.entryPrice', 'Price'),
    /* K  */ t('commodities.valuation.quantity', 'Quantity'),
    /* L  */ t('commodities.valuation.fixingRef', 'Fixing Ref'),
    /* M  */ t('commodities.valuation.marketPrice', 'Market Price'),
    /* N  */ t('commodities.valuation.pnl', 'Unrealised P&L'),
    /* O  */ t('commodities.valuation.currency', 'Currency'),
    /* P  */ t('commodities.valuation.maturityDate', 'Maturity Date'),
  ];

  // ── Header row (row 3) ──
  const headerRow = ws.getRow(3);
  headerRow.values = headers;
  headerRow.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF1E3A5F' },
  };
  headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  headerRow.height = 24;
  headerRow.eachCell((cell) => {
    cell.border = {
      bottom: { style: 'thin', color: { argb: 'FF555555' } },
    };
  });

  // ── Column widths & number formats ──
  const colConfigs: Array<{ width: number; numFmt?: string }> = [
    /* A  */ { width: 16 },                      // Trade Number
    /* B  */ { width: 18 },                      // Entity
    /* C  */ { width: 12 },                      // Product
    /* D  */ { width: 18 },                      // Underlying
    /* E  */ { width: 10 },                      // Direction
    /* F  */ { width: 22 },                      // Counterparty
    /* G  */ { width: 10, numFmt: '#,##0' },     // Lots
    /* H  */ { width: 12, numFmt: '#,##0.00' },  // Lot Size Multiplier
    /* I  */ { width: 10, numFmt: '#,##0.0000' },// QCF
    /* J  */ { width: 14, numFmt: '#,##0.00' },  // Price
    /* K  */ { width: 14, numFmt: '#,##0.0000' },// Quantity
    /* L  */ { width: 12 },                      // Fixing Ref
    /* M  */ { width: 14, numFmt: '#,##0.00' },  // Market Price
    /* N  */ { width: 18, numFmt: '#,##0.00' },  // P&L (formula)
    /* O  */ { width: 10 },                      // Currency
    /* P  */ { width: 14 },                      // Maturity Date
  ];

  colConfigs.forEach((cfg, i) => {
    const col = ws.getColumn(i + 1);
    col.width = cfg.width;
  });

  // ── Data rows (starting at row 4) ──
  const sorted = [...trades].sort((a, b) =>
    a.tradeNumber.localeCompare(b.tradeNumber, undefined, { numeric: true })
  );

  sorted.forEach((trade, idx) => {
    const r = idx + 4; // Excel row number (data starts at row 4)
    const row = ws.getRow(r);

    // A: Trade Number
    row.getCell(1).value = trade.tradeNumber;
    // B: Entity
    row.getCell(2).value = trade.ourParty || '';
    // C: Product
    row.getCell(3).value = trade.productType || '';
    // D: Underlying
    row.getCell(4).value = trade.underlyingName || '';
    // E: Direction
    row.getCell(5).value = trade.direction;
    // F: Counterparty
    row.getCell(6).value = trade.counterpartyName || '';
    // G: Lots
    row.getCell(7).value = trade.lots;
    // H: Lot Size Multiplier
    row.getCell(8).value = trade.lotSizeMultiplier ?? 1;
    // I: QCF
    row.getCell(9).value = trade.quantityConversionFactor ?? 1;
    // J: Price (entry price)
    row.getCell(10).value = trade.entryPrice;
    // K: Quantity
    row.getCell(11).value = trade.quantity ?? 0;
    // L: Fixing Ref
    row.getCell(12).value = trade.fixingReference;
    // M: Market Price
    row.getCell(13).value = trade.marketPrice;

    // N: Unrealised P&L — FORMULA
    // =IF(E{r}="Buy",1,-1)*G{r}*H{r}*I{r}*(M{r}-J{r})
    row.getCell(14).value = {
      formula: `IF(E${r}="Buy",1,-1)*G${r}*H${r}*I${r}*(M${r}-J${r})`,
    } as any;

    // O: Currency
    row.getCell(15).value = trade.currency || '';
    // P: Maturity Date
    row.getCell(16).value = trade.maturityDate || '';

    // Apply number formats to data cells
    row.getCell(7).numFmt = '#,##0';        // Lots
    row.getCell(8).numFmt = '#,##0.00';     // Lot Size
    row.getCell(9).numFmt = '#,##0.0000';   // QCF
    row.getCell(10).numFmt = '#,##0.00';    // Price
    row.getCell(11).numFmt = '#,##0.0000';  // Quantity
    row.getCell(13).numFmt = '#,##0.00';    // Market Price
    row.getCell(14).numFmt = '#,##0.00';    // P&L

    // P&L colouring (use stored value for colour since formula won't be evaluated here)
    const pnl = trade.unrealisedPnl || 0;
    row.getCell(14).font = {
      color: { argb: pnl >= 0 ? 'FF10B981' : 'FFEF4444' },
      bold: true,
    };

    // Direction colouring
    row.getCell(5).font = {
      color: { argb: trade.direction === 'Buy' ? 'FF10B981' : 'FFEF4444' },
      bold: true,
    };
  });

  // ── Totals row ──
  const totalRowNum = sorted.length + 4;
  const totalRow = ws.getRow(totalRowNum);
  totalRow.getCell(13).value = t('commodities.valuation.totalPnl', 'Total P&L');
  totalRow.getCell(13).font = { bold: true, size: 11 };
  totalRow.getCell(13).alignment = { horizontal: 'right' };

  // SUM formula for total P&L
  totalRow.getCell(14).value = {
    formula: `SUM(N4:N${totalRowNum - 1})`,
  } as any;
  totalRow.getCell(14).numFmt = '#,##0.00';
  totalRow.getCell(14).font = { bold: true, size: 11 };

  // ── Auto-filter ──
  ws.autoFilter = {
    from: { row: 3, column: 1 },
    to: { row: sorted.length + 3, column: headers.length },
  };

  // ── Freeze header rows ──
  ws.views = [{ state: 'frozen', ySplit: 3, xSplit: 0 }];

  // ── Download ──
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `commodity_valuation_${valuationDate}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
